<template>
  <modal :show="showModal" @update:show="handleClose" class="no-close-button">
    <div class="add-forwarder-modal">
      <div class="modal-layout">
        <!-- Left side: Form content -->
        <div class="modal-main">
          <div class="modal-header">
            <h2 class="modal-title">{{ modalTitle }} - {{ currentStepTitle }}</h2>
          </div>

          <div class="modal-body">
            <!-- Step 1: Basic Info -->
            <basic-info-step
              v-if="currentStep === 1"
              ref="basicInfoStep"
              v-model="forwarderData"
              :validation-errors="validationErrors"
              :initial-selected-modules="selectedModules"
              :is-edit-mode="isEditMode"
              @update:selectedModules="handleModulesUpdate"
              @name-validation-error="handleNameValidationError"
            />

            <!-- Step 2: System Log -->
            <system-log-step
              v-if="currentStep === 2"
              ref="systemLogStep"
              v-model="forwarderData.system_log"
              :index-names="indexNames"
              :windows-source-types="windowsSourceTypes"
              :linux-source-types="linuxSourceTypes"
              :is-disabled="!selectedModules.system_log"
              :validation-errors="validationErrors"
            />

            <!-- Step 3: Resource Monitor -->
            <resource-monitor-step
              v-if="currentStep === 3"
              ref="resourceMonitorStep"
              v-model="forwarderData.resource_monitor"
              :index-names="indexNames"
              :resource-monitor-source-types="resourceMonitorSourceTypes"
              :is-disabled="!selectedModules.resource_monitor"
              :validation-errors="validationErrors"
            />

            <!-- Step 4: File Log -->
            <file-log-step
              v-if="currentStep === 4"
              ref="fileLogStep"
              v-model="forwarderData.file_log"
              :index-names="indexNames"
              :file-log-source-types="fileLogSourceTypes"
              :is-disabled="!selectedModules.file_log"
              :validation-errors="validationErrors"
            />
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="button cancel-button"
              @click="handleCancel"
            >
              {{ $t('cancel') }}
            </button>
            <button
              type="button"
              class="button is-primary"
              :disabled="!canSave || isSaving"
              @click="handleSubmit"
            >
              <span v-if="!isSaving">{{ $t('save') }}</span>
              <span v-else class="is-loading">{{ $t('saving') }}</span>
            </button>
          </div>
        </div>

        <!-- Right side: Step navigation -->
        <div class="modal-sidebar">
          <div class="step-navigation">
            <h3 class="sidebar-title">{{ $t('forwarders.currentStep') }}</h3>
            <div class="step-list">
              <div
                v-for="(step, index) in steps"
                :key="index"
                class="step-item"
                :class="{
                  'active': currentStep === index + 1,
                  'clickable': canNavigateToStep(index + 1),
                  'disabled': !canNavigateToStep(index + 1)
                }"
                @click="navigateToStep(index + 1)"
              >
                <div class="step-number">
                  <span>{{ index + 1 }}</span>
                </div>
                <div class="step-name">{{ step.title }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <modal :show.sync="showCancelConfirm" class="cancel-confirm-modal">
      <div class="confirm-box">
        <div class="confirm-header">
          <h3>{{ $t('forwarders.cancelCreateTitle') }}</h3>
        </div>
        <div class="confirm-body">
          <p>{{ $t('forwarders.cancelCreateMessage') }}</p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="button cancel-button"
            @click="showCancelConfirm = false"
          >
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            @click="confirmCancel"
          >
            {{ $t('forwarders.confirm') }}
          </button>
        </div>
      </div>
    </modal>
  </modal>
</template>

<script>
import { Modal } from '@cyradar/ui'
import BasicInfoStep from './steps/BasicInfoStep.vue'
import SystemLogStep from './steps/SystemLogStep.vue'
import ResourceMonitorStep from './steps/ResourceMonitorStep.vue'
import FileLogStep from './steps/FileLogStep.vue'

export default {
  name: 'AddForwarderModal',
  components: {
    Modal,
    BasicInfoStep,
    SystemLogStep,
    ResourceMonitorStep,
    FileLogStep
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    indexNames: {
      type: [Object, Array],
      default: () => ({})
    },
    windowsSourceTypes: {
      type: Array,
      default: () => []
    },
    linuxSourceTypes: {
      type: Array,
      default: () => []
    },
    resourceMonitorSourceTypes: {
      type: Array,
      default: () => []
    },
    fileLogSourceTypes: {
      type: Array,
      default: () => []
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    editItem: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      currentStep: 1,
      showCancelConfirm: false,
      isSaving: false,
      validationErrors: {},
      nameValidationError: false,
      selectedModules: {
        system_log: false,
        resource_monitor: false,
        file_log: false
      },
      forwarderData: {
        name: '',
        description: '',
        token: '',
        enabled: true,
        system_log: {
          enabled: false,
          log_send_interval_seconds: 600,
          batch_size: 500,
          windows: {
            enabled: false,
            index_id: '',
            source_type_id: '',
            channels: [],
            custom_paths: []
          },
          linux: {
            enabled: false,
            index_id: '',
            source_type_id: '',
            channels: [],
            custom_paths: []
          }
        },
        resource_monitor: {
          enabled: false,
          index_id: '',
          source_type_id: '',
          check_interval_seconds: 60,
          log_send_interval_seconds: 600,
          batch_size: 500,
          metrics: []
        },
        file_log: {
          enabled: false,
          log_send_interval_seconds: 15,
          batch_size: 100,
          windows: {
            enabled: false,
            streams: []
          },
          linux: {
            enabled: false,
            streams: []
          }
        }
      }
    }
  },
  computed: {
    showModal() {
      return this.show
    },
    modalTitle() {
      return this.isEditMode ? (this.forwarderData.name || this.$t('editForwarder')) : this.$t('newForwarder')
    },
    steps() {
      return [
        { title: this.$t('forwarders.basicInfo'), key: 'basic' },
        { title: this.$t('forwarders.systemLog'), key: 'system_log' },
        { title: this.$t('forwarders.resourceMonitor'), key: 'resource_monitor' },
        { title: this.$t('forwarders.fileLog'), key: 'file_log' }
      ]
    },
    currentStepTitle() {
      return this.steps[this.currentStep - 1]?.title || ''
    },
    canSave() {
      // Check if name has validation error (from BasicInfoStep)
      if (this.nameValidationError) {
        return false
      }
      
      // Validate name
      const name = this.forwarderData.name.trim()
      if (!name || name.length < 3 || name.length > 30) {
        return false
      }
      // Check for invalid characters (only allow alphanumeric, dash, underscore, dot)
      const validNamePattern = /^[a-zA-Z0-9._-]+$/
      if (!validNamePattern.test(name)) {
        return false
      }
      
      // Validate token (required)
      if (!this.forwarderData.token || this.forwarderData.token.trim().length === 0) {
        return false
      }
      
      // If no module selected, can save (modules are optional)
      if (!this.hasAtLeastOneModuleSelected) {
        return true
      }
      // Validate selected modules
      if (this.selectedModules.system_log && !this.validateSystemLogStep()) {
        return false
      }
      if (this.selectedModules.resource_monitor && !this.validateResourceMonitorStep()) {
        return false
      }
      if (this.selectedModules.file_log && !this.validateFileLogStep()) {
        return false
      }
      return true
    },
    hasAtLeastOneModuleSelected() {
      return this.selectedModules.system_log || 
             this.selectedModules.resource_monitor || 
             this.selectedModules.file_log
    },
  },
  watch: {
    show(val) {
      if (val) {
        if (this.isEditMode && this.editItem) {
          this.loadEditData()
        } else {
          this.resetForm()
          this.generateToken()
        }
      }
    },
    editItem: {
      handler(newVal) {
        if (newVal && this.isEditMode && this.show) {
          this.loadEditData()
        }
      },
      deep: true,
      immediate: true
    },
    forwarderData: {
      handler() {
        // Force re-compute canSave when forwarderData changes
        this.$forceUpdate()
      },
      deep: true
    },
    selectedModules: {
      handler(newVal, oldVal) {
        // Clear data when module is deselected
        if (oldVal) {
          // System Log deselected
          if (oldVal.system_log && !newVal.system_log) {
            this.clearSystemLogData()
          }
          // Resource Monitor deselected
          if (oldVal.resource_monitor && !newVal.resource_monitor) {
            this.clearResourceMonitorData()
          }
          // File Log deselected
          if (oldVal.file_log && !newVal.file_log) {
            this.clearFileLogData()
          }
        }
        
        // Force re-compute canSave when selectedModules changes
        this.$forceUpdate()
      },
      deep: true
    }
  },
  methods: {
    handleModulesUpdate(modules) {
      this.selectedModules = { ...modules }
    },
    handleNameValidationError(hasError) {
      this.nameValidationError = hasError
    },
    generateToken() {
      // Generate a random token
      const token = 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/x/g, () => {
        return Math.floor(Math.random() * 16).toString(16)
      })
      this.forwarderData.token = token
    },
    clearSystemLogData() {
      // Reset system_log to default values
      this.forwarderData.system_log = {
        enabled: false,
        log_send_interval_seconds: 600,
        batch_size: 500,
        windows: {
          enabled: false,
          index_id: '',
          source_type_id: '',
          channels: [],
          custom_paths: []
        },
        linux: {
          enabled: false,
          index_id: '',
          source_type_id: '',
          channels: [],
          custom_paths: []
        }
      }
      // If currently on system_log step, go back to step 1
      if (this.currentStep === 2) {
        this.currentStep = 1
      }
    },
    clearResourceMonitorData() {
      // Reset resource_monitor to default values
      this.forwarderData.resource_monitor = {
        enabled: false,
        index_id: '',
        source_type_id: '',
        check_interval_seconds: 60,
        log_send_interval_seconds: 600,
        batch_size: 500,
        metrics: []
      }
      // If currently on resource_monitor step, go back to step 1
      if (this.currentStep === 3) {
        this.currentStep = 1
      }
    },
    clearFileLogData() {
      // Reset file_log to default values
      this.forwarderData.file_log = {
        enabled: false,
        log_send_interval_seconds: 15,
        batch_size: 100,
        windows: {
          enabled: false,
          streams: []
        },
        linux: {
          enabled: false,
          streams: []
        }
      }
      // If currently on file_log step, go back to step 1
      if (this.currentStep === 4) {
        this.currentStep = 1
      }
    },
    validateSystemLogStep() {
      const systemLog = this.forwarderData.system_log
      
      // Check common required fields
      // Khoảng thời gian: min 3, max 86400
      if (!systemLog.log_send_interval_seconds || 
          systemLog.log_send_interval_seconds < 3 || 
          systemLog.log_send_interval_seconds > 86400) {
        return false
      }
      // Số lượng nhật ký: min 1, max 999999
      if (!systemLog.batch_size || 
          systemLog.batch_size < 1 || 
          systemLog.batch_size > 999999) {
        return false
      }
      
      // At least one OS must be enabled
      if (!systemLog.windows.enabled && !systemLog.linux.enabled) {
        return false
      }
      
      // Validate Windows if enabled
      if (systemLog.windows.enabled) {
        if (!systemLog.windows.index_id || !systemLog.windows.source_type_id || systemLog.windows.channels.length === 0) {
          return false
        }
      }
      
      // Validate Linux if enabled
      if (systemLog.linux.enabled) {
        if (!systemLog.linux.index_id || !systemLog.linux.source_type_id || systemLog.linux.channels.length === 0) {
          return false
        }
      }
      
      return true
    },
    validateResourceMonitorStep() {
      const resourceMonitor = this.forwarderData.resource_monitor
      
      // Check required fields
      if (!resourceMonitor.check_interval_seconds || resourceMonitor.check_interval_seconds < 1) {
        return false
      }
      if (!resourceMonitor.log_send_interval_seconds || resourceMonitor.log_send_interval_seconds < 1) {
        return false
      }
      if (!resourceMonitor.batch_size || resourceMonitor.batch_size < 1) {
        return false
      }
      if (!resourceMonitor.index_id || !resourceMonitor.source_type_id) {
        return false
      }
      
      return true
    },
    validateFileLogStep() {
      const fileLog = this.forwarderData.file_log
      
      // Check common required fields
      if (!fileLog.log_send_interval_seconds || fileLog.log_send_interval_seconds < 1) {
        return false
      }
      if (!fileLog.batch_size || fileLog.batch_size < 1) {
        return false
      }
      
      // At least one OS must be enabled
      if (!fileLog.windows.enabled && !fileLog.linux.enabled) {
        return false
      }
      
      // Validate Windows streams if enabled
      if (fileLog.windows.enabled) {
        if (!fileLog.windows.streams || fileLog.windows.streams.length === 0) {
          return false
        }
        // Check each stream has required fields
        for (const stream of fileLog.windows.streams) {
          if (!stream.name || !stream.index_id || !stream.source_type_id || 
              !stream.paths || stream.paths.length === 0) {
            return false
          }
        }
      }
      
      // Validate Linux streams if enabled
      if (fileLog.linux.enabled) {
        if (!fileLog.linux.streams || fileLog.linux.streams.length === 0) {
          return false
        }
        // Check each stream has required fields
        for (const stream of fileLog.linux.streams) {
          if (!stream.name || !stream.index_id || !stream.source_type_id || 
              !stream.paths || stream.paths.length === 0) {
            return false
          }
        }
      }
      
      return true
    },
    handleCancel() {
      this.showCancelConfirm = true
    },
    confirmCancel() {
      this.showCancelConfirm = false
      this.$emit('update:show', false)
      this.resetForm()
    },
    handleClose(value) {
      // Prevent modal from closing on outside click or ESC
      // Only allow closing through Cancel button
      // Do nothing - modal will only close through confirmCancel()
    },
    canNavigateToStep(stepNumber) {
      // Step 1 is always accessible
      if (stepNumber === 1) return true
      
      // Check if the target step's module is selected
      const targetStepModuleSelected = (
        (stepNumber === 2 && this.selectedModules.system_log) ||
        (stepNumber === 3 && this.selectedModules.resource_monitor) ||
        (stepNumber === 4 && this.selectedModules.file_log)
      )
      
      // Cannot navigate to unselected module steps
      if (!targetStepModuleSelected) {
        return false
      }
      
      // If we're on Step 1, can navigate to any selected module step
      if (this.currentStep === 1) {
        return true
      }
      
      // If we're on a module step (2, 3, or 4)
      if (this.currentStep !== 1 && stepNumber !== 1) {
        // Check if current step is complete
        const currentStepComplete = this.isCurrentStepComplete()
        
        // If current step is incomplete, cannot navigate to other module steps
        if (!currentStepComplete) {
          return false
        }
        
        // If current step is complete, can navigate to other selected module steps
        return true
      }
      
      return false
    },
    isCurrentStepComplete() {
      // Check if current step has all required information filled
      if (this.currentStep === 1) return true // Step 1 is always considered complete for navigation
      if (this.currentStep === 2) return this.validateSystemLogStep()
      if (this.currentStep === 3) return this.validateResourceMonitorStep()
      if (this.currentStep === 4) return this.validateFileLogStep()
      return false
    },
    navigateToStep(stepNumber) {
      if (!this.canNavigateToStep(stepNumber)) {
        return
      }
      this.currentStep = stepNumber
    },
    resetForm() {
      this.currentStep = 1
      this.validationErrors = {}
      this.selectedModules = {
        system_log: false,
        resource_monitor: false,
        file_log: false
      }
      this.forwarderData = {
        name: '',
        description: '',
        token: '',
        enabled: true,
        system_log: {
          enabled: false,
          log_send_interval_seconds: 600,
          batch_size: 500,
          windows: {
            enabled: false,
            index_id: '',
            source_type_id: '',
            channels: [],
            custom_paths: []
          },
          linux: {
            enabled: false,
            index_id: '',
            source_type_id: '',
            channels: [],
            custom_paths: []
          }
        },
        resource_monitor: {
          enabled: false,
          index_id: '',
          source_type_id: '',
          check_interval_seconds: 60,
          log_send_interval_seconds: 600,
          batch_size: 500,
          metrics: []
        },
        file_log: {
          enabled: false,
          log_send_interval_seconds: 15,
          batch_size: 100,
          windows: {
            enabled: false,
            streams: []
          },
          linux: {
            enabled: false,
            streams: []
          }
        }
      }
    },
    async handleSubmit() {
      if (!this.canSave) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('forwarders.pleaseCompleteAllRequiredFields')
        })
        return
      }

      this.isSaving = true

      try {
        // Set enabled flags based on selected modules
        this.forwarderData.system_log.enabled = this.selectedModules.system_log
        this.forwarderData.resource_monitor.enabled = this.selectedModules.resource_monitor
        this.forwarderData.file_log.enabled = this.selectedModules.file_log

        // Normalize payload
        const payload = this.normalizePayload(this.forwarderData)

        // Submit to API
        const apiMethod = this.isEditMode ? 'patch' : 'post'
        const apiUrl = this.isEditMode ? `/api/v1/forwarders/${this.forwarderData.id}` : '/api/v1/forwarders'
        const response = await this.$http[apiMethod](apiUrl, payload)

        if (response && response.data) {
          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: this.isEditMode ? this.$t('forwarders.updateForwarderSuccess') : this.$t('forwarders.addForwarderSuccess')
          })
          this.$emit('success')
          this.$emit('update:show', false)
          this.resetForm()
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || (this.isEditMode ? this.$t('forwarders.updateForwarderFailed') : this.$t('forwarders.addForwarderFailed'))
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: errorMessage
        })
      } finally {
        this.isSaving = false
      }
    },
    loadEditData() {
      if (!this.editItem) return
      
      // Deep clone the edit item
      this.forwarderData = JSON.parse(JSON.stringify(this.editItem))
      
      // Convert empty string or "000000000000000000000000" index_id to null for proper placeholder display in v-select
      const isEmptyIndexId = (id) => !id || id === '' || id === '000000000000000000000000'
      
      if (this.forwarderData.system_log) {
        if (this.forwarderData.system_log.windows && isEmptyIndexId(this.forwarderData.system_log.windows.index_id)) {
          this.forwarderData.system_log.windows.index_id = null
        }
        if (this.forwarderData.system_log.linux && isEmptyIndexId(this.forwarderData.system_log.linux.index_id)) {
          this.forwarderData.system_log.linux.index_id = null
        }
      }
      if (this.forwarderData.resource_monitor && isEmptyIndexId(this.forwarderData.resource_monitor.index_id)) {
        this.forwarderData.resource_monitor.index_id = null
      }
      
      // Set selected modules based on enabled states
      this.selectedModules = {
        system_log: this.editItem.system_log?.enabled || false,
        resource_monitor: this.editItem.resource_monitor?.enabled || false,
        file_log: this.editItem.file_log?.enabled || false
      }
      
      this.currentStep = 1
      this.validationErrors = {}
    },
    normalizePayload(data) {
      const payload = JSON.parse(JSON.stringify(data))
      
      if (payload.system_log) {
        payload.system_log.log_send_interval_seconds = parseInt(payload.system_log.log_send_interval_seconds)
        payload.system_log.batch_size = parseInt(payload.system_log.batch_size)
      }
      
      if (payload.resource_monitor) {
        payload.resource_monitor.check_interval_seconds = parseInt(payload.resource_monitor.check_interval_seconds)
        payload.resource_monitor.log_send_interval_seconds = parseInt(payload.resource_monitor.log_send_interval_seconds)
        payload.resource_monitor.batch_size = parseInt(payload.resource_monitor.batch_size)
      }
      
      if (payload.file_log) {
        payload.file_log.log_send_interval_seconds = parseInt(payload.file_log.log_send_interval_seconds)
        payload.file_log.batch_size = parseInt(payload.file_log.batch_size)
      }
      
      return payload
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/dark-modal.scss';

// Hide the close button (X) from the modal
::v-deep .modal-close {
  display: none !important;
}

.add-forwarder-modal {
  .modal-layout {
    display: flex;
    min-height: 600px;
    background-color: $modal-bg;
    border-radius: 8px;
    overflow: hidden;
  }

  .modal-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    min-width: 0;
  }

  .modal-header {
    margin-bottom: 1.25rem;

    .modal-title {
      color: $modal-text;
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0;
    }
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 1.5rem;
    max-height: calc(100vh - 300px);
    min-height: 300px;
    padding-right: 1rem;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid $modal-separator;

    .button {
      &.cancel-button {
        @include dark-modal-cancel-button;
      }

      &.is-primary {
        @include dark-modal-primary-button;
      }
    }
  }

  .modal-sidebar {
    width: 280px;
    background-color: #151824;
    padding: 1.5rem 1rem;
    border-left: 1px solid $modal-separator;

    .sidebar-title {
      color: $modal-text;
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 1rem;
    }

    .step-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .step-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      border-radius: 8px;
      background-color: rgba(255, 255, 255, 0.05);
      transition: all 0.2s;

      &.clickable {
        cursor: pointer;

        &:hover:not(.disabled) {
          background-color: rgba(255, 255, 255, 0.1);
        }
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;

        .step-name {
          color: rgba(255, 255, 255, 0.5);
        }
      }

      &.active {
        background-color: $modal-primary;
        box-shadow: 0 2px 8px rgba(31, 111, 244, 0.3);

        .step-number {
          background-color: $modal-text;
          color: $modal-primary;
        }

        .step-name {
          font-weight: 600;
        }
      }

      .step-number {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.1);
        color: $modal-text;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.85rem;
        flex-shrink: 0;
      }

      .step-name {
        color: $modal-text;
        font-size: 0.85rem;
        line-height: 1.3;
      }
    }
  }
}

.cancel-confirm-modal {
  .confirm-box {
    background-color: $modal-bg;
    border-radius: 8px;
    padding: 1.5rem;
    max-width: 500px;

    .confirm-header {
      margin-bottom: 1rem;

      h3 {
        color: $modal-text;
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
      }
    }

    .confirm-body {
      margin-bottom: 1.5rem;

      p {
        color: $modal-text-secondary;
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
      }
    }

    .confirm-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;

      .button {
        &.cancel-button {
          @include dark-modal-cancel-button;
        }

        &.is-primary {
          @include dark-modal-primary-button;
        }
      }
    }
  }
}
</style>
